import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { HiFingerPrint } from "react-icons/hi";
import { toast } from "react-toastify";
import moment from "moment";
import useAuth from "../../../../hooks/useAuth";
import SecureStorage from "../../../../utils/secureStorage";
import { CustomButton } from "../../../atoms/CustomButton";
import { AlertMessage } from "../../../atoms/AlertMessage";
import { ModalShell } from "../../../atoms/ModalShell";
import {
  forgetPasskeyInBrowser,
  isPasskeySupported,
  notifyPasskeyError,
  useListPasskeys,
  useRegisterPasskey,
  useDeletePasskey,
} from "../../../../hooks/auth/passwordless";

type Passkey = {
  credential_identification: string;
  label: string | null;
  transports: string[];
  device_type: string | null;
  backed_up: boolean;
  rp_id: string | null;
  creation_date_time: number | null;
  last_used_date_time: number | null;
};

const PasskeyManager = () => {
  const [t] = useTranslation();
  const { user } = useAuth();

  const [passkeys, setPasskeys] = useState<Passkey[]>([]);
  const [pendingDeletion, setPendingDeletion] = useState<Passkey | null>(null);
  const [hasLoaded, setHasLoaded] = useState(false);

  const isImpersonating = SecureStorage.isImpersonationTab();

  const { mutateAsync: fetchPasskeys } = useListPasskeys();
  const { mutate: registerPasskey, isLoading: isRegistering } =
    useRegisterPasskey();
  const { mutate: deletePasskey, isLoading: isDeleting } = useDeletePasskey();

  const scope = {
    entity_identification: user?.entity_identification,
    user_identification: user?.user_identification,
  };

  const refresh = useCallback(async () => {
    if (!scope.entity_identification || !scope.user_identification) {
      return;
    }
    try {
      const response = await fetchPasskeys(scope as any);
      setPasskeys(response?.data?.element || []);
    } catch {
      setPasskeys([]);
    } finally {
      setHasLoaded(true);
    }
    // scope is derived from user on every render; the identifiers are what matter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope.entity_identification, scope.user_identification, fetchPasskeys]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const onAdd = () => {
    registerPasskey(scope as any, {
      onSuccess: () => {
        toast.success(t("passkey.added"));
        refresh();
      },
      onError: (error: any) => {
        notifyPasskeyError(error, t, toast);
      },
    });
  };

  const onConfirmDelete = () => {
    if (!pendingDeletion) {
      return;
    }
    deletePasskey(
      {
        ...(scope as any),
        credential_identification: pendingDeletion.credential_identification,
      },
      {
        onSuccess: async () => {
          await forgetPasskeyInBrowser(
            pendingDeletion.rp_id || undefined,
            pendingDeletion.credential_identification
          );
          toast.success(t("passkey.deleted"));
          setPendingDeletion(null);
          refresh();
        },
        onError: (error: any) => {
          notifyPasskeyError(error, t, toast);
        },
      }
    );
  };

  if (!isPasskeySupported()) {
    return (
      <div className="px-5 pt-5 pb-8 bg-white rounded-lg border border-gray-200 flex-col justify-start items-start gap-5 inline-flex w-full">
        <div className="self-stretch flex-col justify-start items-start gap-2 flex">
          <div className="flex items-center gap-2">
            <HiFingerPrint
              size={20}
              className="shrink-0 text-blue-600"
              aria-hidden="true"
            />
            <div className="text-gray-700 text-lg font-semibold font-figtree leading-7">
              {t("dashboard.profile.passkeys")}
            </div>
          </div>
          <div className="text-gray-700 text-sm font-normal font-figtree leading-tight">
            {t("passkey.description")}
          </div>
        </div>
        <AlertMessage color="gray">{t("passkey.unsupported")}</AlertMessage>
      </div>
    );
  }

  return (
    <div className="px-5 pt-5 pb-8 bg-white rounded-lg border border-gray-200 flex-col justify-start items-start gap-5 inline-flex w-full">
      <div className="self-stretch flex-col justify-start items-start gap-2 flex">
        <div className="flex items-center gap-2">
          <HiFingerPrint
            size={20}
            className="shrink-0 text-blue-600"
            aria-hidden="true"
          />
          <div className="text-gray-700 text-lg font-semibold font-figtree leading-7">
            {t("dashboard.profile.passkeys")}
          </div>
        </div>
        <div className="text-gray-700 text-sm font-normal font-figtree leading-tight">
          {t("passkey.description")}
        </div>
      </div>
      <div className="flex w-full flex-col gap-4">
        <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-start">
          <div className="min-w-0 flex-1 sm:basis-64">
            <p className="text-sm font-medium text-gray-900">
              {t("passkey.registeredCount", { count: passkeys.length })}
            </p>
          </div>
          {!isImpersonating && (
            <div className="flex flex-wrap items-center gap-2">
              <CustomButton
                variant="primary"
                size="md"
                icon="plus"
                title={t("passkey.addBtn")}
                isLoading={isRegistering}
                disabled={isRegistering}
                onClick={onAdd}
              />
            </div>
          )}
        </div>

        {isImpersonating && (
          <AlertMessage color="info">
            {t("passkey.impersonationReadOnly")}
          </AlertMessage>
        )}

        {hasLoaded && passkeys.length === 0 ? (
          <p className="text-sm text-gray-500">{t("passkey.empty")}</p>
        ) : (
          <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200">
            {passkeys.map((passkey) => (
              <li
                key={passkey.credential_identification}
                className="flex flex-col gap-3 p-3 sm:flex-row sm:flex-wrap sm:items-start"
              >
                <div className="min-w-0 flex-1 sm:basis-64">
                  <p className="truncate text-sm font-semibold text-gray-900">
                    {passkey.label || t("passkey.unnamed")}
                  </p>
                  <dl className="mt-1 grid grid-cols-[7.5rem_1fr] items-center gap-x-3 gap-y-1 text-xs">
                    <dt className="text-gray-500">{t("passkey.addedOn")}</dt>
                    <dd className="text-gray-700">
                      {passkey.creation_date_time
                        ? moment(passkey.creation_date_time).format("ll")
                        : "—"}
                    </dd>
                    <dt className="text-gray-500">{t("passkey.lastUsed")}</dt>
                    <dd className="text-gray-700">
                      {passkey.last_used_date_time
                        ? moment(passkey.last_used_date_time).format("lll")
                        : t("passkey.neverUsed")}
                    </dd>
                    <dt className="text-gray-500">{t("passkey.syncLabel")}</dt>
                    <dd className="text-gray-700">
                      {passkey.backed_up
                        ? t("passkey.synced")
                        : t("passkey.deviceBound")}
                    </dd>
                  </dl>
                </div>
                {!isImpersonating && (
                  <div className="flex flex-wrap items-center gap-2">
                    <CustomButton
                      variant="secondary"
                      size="sm"
                      icon="trash"
                      title={t("passkey.removeBtn")}
                      onClick={() => setPendingDeletion(passkey)}
                    />
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}

        <ModalShell
          show={Boolean(pendingDeletion)}
          onClose={() => setPendingDeletion(null)}
          size="md"
          title={t("passkey.removeTitle")}
          subtitle={t("passkey.removeSubtitle")}
          footer={
            <>
              <CustomButton
                variant="secondary"
                size="md"
                title={t("common.cancel")}
                onClick={() => setPendingDeletion(null)}
              />
              <CustomButton
                variant="danger"
                size="md"
                title={t("passkey.removeBtn")}
                isLoading={isDeleting}
                disabled={isDeleting}
                onClick={onConfirmDelete}
              />
            </>
          }
        >
          <p className="text-sm text-gray-700">
            {pendingDeletion?.label || t("passkey.unnamed")}
          </p>
        </ModalShell>
      </div>
    </div>
  );
};

export default PasskeyManager;
